import { fetchWithAuth } from '@/lib/api'
import { groupThreadsByDateRange as groupThreadsByDateRangeImpl } from '@/lib/api/threads-grouping'

export type AgentChatThread = {
  id: string
  organizationId: string | null
  kind: string
  title: string
  status: string
  pinned: boolean
  snoozedUntil: string | null
  workspaceId: string | null
  taggedOrganizationIds?: string[]
  metadata: Record<string, unknown>
  linkedConversationId?: string
  createdAt: string
  updatedAt: string
  lastMessageAt: string | null
}

export type ListThreadsResponse = {
  threads: AgentChatThread[]
  count: number
  limit: number
  offset: number
  hasMore: boolean
  generatedAt: string
}

export type ListThreadsParams = {
  kind?: string
  search?: string
  limit?: number
  offset?: number
  status?: string
  /** Comma-separated kinds to omit (e.g. agent_chat for Inbox triage). */
  excludeKind?: string
}

/** Fetch threads from GET /api/threads with optional filters. */
export async function listThreads(params: ListThreadsParams = {}): Promise<ListThreadsResponse> {
  const qs = new URLSearchParams()
  if (params.kind) qs.set('kind', params.kind)
  if (params.search) qs.set('search', params.search)
  if (params.limit != null) qs.set('limit', String(params.limit))
  if (params.offset != null) qs.set('offset', String(params.offset))
  if (params.status) qs.set('status', params.status)
  if (params.excludeKind) qs.set('exclude_kind', params.excludeKind)
  const query = qs.toString()
  const res = await fetchWithAuth(`/api/threads${query ? `?${query}` : ''}`)
  if (!res.ok) {
    throw new Error(`Failed to list threads (${res.status})`)
  }
  return res.json()
}

/** Fetch a single thread by id. */
export async function getThread(threadId: string): Promise<AgentChatThread> {
  const res = await fetchWithAuth(`/api/threads/${encodeURIComponent(threadId)}`)
  if (!res.ok) {
    const error = new Error(`Failed to fetch thread (${res.status})`) as Error & { status: number }
    error.status = res.status
    throw error
  }
  const body = (await res.json()) as AgentChatThread | { thread: AgentChatThread }
  if (body && typeof body === 'object' && 'thread' in body && body.thread) {
    return body.thread
  }
  return body as AgentChatThread
}

/** PATCH thread title, status, or pinned flag. */
export async function patchThread(
  threadId: string,
  patch: { title?: string; status?: string; pinned?: boolean },
): Promise<AgentChatThread> {
  const res = await fetchWithAuth(`/api/threads/${encodeURIComponent(threadId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  if (!res.ok) {
    throw new Error(`Failed to update thread (${res.status})`)
  }
  return res.json()
}

/**
 * AI suggest thread title from linked conversation (preview only).
 * Pass prior suggestions in `avoid` to shuffle.
 */
export async function suggestThreadTitle(
  threadId: string,
  options?: { avoid?: string[] },
): Promise<{
  success: boolean
  title: string
  message?: string
  model?: string
  conversationId?: string
}> {
  const res = await fetchWithAuth(`/api/threads/${encodeURIComponent(threadId)}/suggest-title`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ avoid: options?.avoid ?? [] }),
  })
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}))
    throw new Error(
      (errBody as { error?: string }).error || `Failed to suggest thread title (${res.status})`,
    )
  }
  return res.json()
}

/** DELETE thread and soft-delete linked ai_conversation when present. */
export async function deleteThread(threadId: string): Promise<void> {
  const res = await fetchWithAuth(`/api/threads/${encodeURIComponent(threadId)}`, {
    method: 'DELETE',
  })
  if (!res.ok) {
    throw new Error(`Failed to delete thread (${res.status})`)
  }
}

/** Default page size when loading agent_chat threads into the sidebar. */
export const AGENT_CHAT_SIDEBAR_PAGE_SIZE = 30

/** @deprecated use AGENT_CHAT_SIDEBAR_PAGE_SIZE */
export const AGENT_CHAT_SIDEBAR_LIMIT = AGENT_CHAT_SIDEBAR_PAGE_SIZE

export type AgentChatThreadsPage = {
  threads: AgentChatThread[]
  hasMore: boolean
  offset: number
  limit: number
}

/** List one page of agent_chat threads for the Chats sidebar. */
export async function listAgentChatThreadsPage(
  options: {
    search?: string
    limit?: number
    offset?: number
    /** Omit to exclude archived; pass `archived` for the archive view. */
    status?: string
  } = {},
): Promise<AgentChatThreadsPage> {
  const limit = options.limit ?? AGENT_CHAT_SIDEBAR_PAGE_SIZE
  const offset = options.offset ?? 0
  const result = await listThreads({
    kind: 'agent_chat',
    search: options.search,
    limit,
    offset,
    status: options.status,
  })
  return {
    threads: result.threads,
    hasMore: Boolean(result.hasMore),
    offset: result.offset ?? offset,
    limit: result.limit ?? limit,
  }
}

/** List agent_chat threads for the Chats sidebar (first page only). */
export async function listAgentChatThreads(
  search?: string,
  limit = AGENT_CHAT_SIDEBAR_PAGE_SIZE,
): Promise<AgentChatThread[]> {
  const result = await listAgentChatThreadsPage({ search, limit, offset: 0 })
  return result.threads
}

export {
  groupThreadsByDateRange,
  CHAT_SIDEBAR_SECTION_PREVIEW,
  CHAT_SIDEBAR_DEFAULT_COLLAPSED,
  splitPinnedThreads,
  shouldShowThreadSubtitle,
  touchThreadActivity,
} from '@/lib/api/threads-grouping'
export type {
  ChatDateRangeBucket,
  ChatSidebarSectionKey,
  ChatThreadDateGroup,
} from '@/lib/api/threads-grouping'

/** @deprecated use groupThreadsByDateRange */
export function groupThreadsByDay(threads: AgentChatThread[]): { label: string; items: AgentChatThread[] }[] {
  return groupThreadsByDateRangeImpl(threads).map((g) => ({ label: g.label, items: g.items }))
}

/** Format thread/message timestamp (Cursor-style relative, then date). */
export function formatThreadActivity(
  iso: string | null | undefined,
  now = new Date(),
): string {
  if (!iso) return ''
  const d = new Date(iso)
  const diffMs = Math.max(0, now.getTime() - d.getTime())
  const diffMins = Math.floor(diffMs / 60_000)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 48) return `${diffHours}h ago`
  if (diffDays < 14) return `${diffDays}d ago`

  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/** @deprecated use formatThreadActivity */
export function formatThreadDate(iso: string | null | undefined): string {
  return formatThreadActivity(iso)
}
